import produce from "immer";
import { create } from "zustand";

import { OriginInfo, UpdateProductStockSuccessPayload } from "./types";
import { InventoryProduct } from "../../../shared/models/inventoryProduct/types";

type ScanType = "product" | "shelf" | "search";

type Phase =
  | "idle"
  | "resolvingProducts"
  | "search"
  | "searchLoading"
  | "selectProduct"
  | "declareStockCorrections"
  | "declareStockCorrectionsLoading";

export type StockCorrectionState = {
  phase: Phase;
  selectedInventoryProductSku: string | null;
  selectedInventoryProductShelf: string | null;
  inventoryProductsList: InventoryProduct[];
  scanType: ScanType | null;
  scannedValue: string | null;
  stockCorrectionId: string | null;
  originInfo: OriginInfo;
};

type StockCorrectionActions = {
  // Phase transitions
  enterIdle: () => void;
  setPhase: (phase: Phase) => void;

  // Context setters
  setScanType: (scanType: ScanType) => void;
  setScannedValue: (value: string) => void;
  setOriginInfo: (originInfo: OriginInfo) => void;
  resetOriginInfo: () => void;
  setInventoryProductsList: (products: InventoryProduct[]) => void;
  resetInventoryProductsList: () => void;
  setInventoryProduct: (sku: string, shelf: string | null) => void;
  validateInventoryProductStock: (sku: string) => void;
  updateInventoryProductStock: (payload: UpdateProductStockSuccessPayload) => void;
  setStockCorrectionId: () => void;
  resetStockCorrectionId: () => void;

  // Full reset
  reset: () => void;
};

const initialState: StockCorrectionState = {
  phase: "idle",
  selectedInventoryProductSku: null,
  selectedInventoryProductShelf: null,
  inventoryProductsList: [],
  scanType: null,
  scannedValue: null,
  stockCorrectionId: null,
  originInfo: null,
};

export const useStockCorrectionStore = create<StockCorrectionState & StockCorrectionActions>()(
  (set, get) => ({
    ...initialState,

    enterIdle: () => set({ phase: "idle", scanType: null, scannedValue: null }),
    setPhase: (phase) => set({ phase }),

    setScanType: (scanType) => set({ scanType }),

    setScannedValue: (value) => set({ scannedValue: value }),

    setOriginInfo: (originInfo) => set({ originInfo }),
    resetOriginInfo: () => set({ originInfo: null }),

    setInventoryProductsList: (products) => set({ inventoryProductsList: products }),
    resetInventoryProductsList: () => set({ inventoryProductsList: [] }),

    setInventoryProduct: (sku, shelf) =>
      set({ selectedInventoryProductSku: sku, selectedInventoryProductShelf: shelf }),

    validateInventoryProductStock: (sku) => {
      set(
        produce((state: StockCorrectionState) => {
          const product = state.inventoryProductsList.find(
            (p) => p.productSku === sku && p.stockStatus === "unchanged",
          );
          if (product) {
            product.stockStatus = "validated";
          }
        }),
      );
    },

    updateInventoryProductStock: (payload) => {
      set(
        produce((state: StockCorrectionState) => {
          const product = state.inventoryProductsList.find(
            (p) => p.productSku === payload.productSku,
          );
          if (product) {
            product.stockStatus = product.initialStock !== payload.after ? "updated" : "validated";
            product.stock = payload.after;
          }
        }),
      );
    },

    setStockCorrectionId: () => {
      const current = get().stockCorrectionId;
      if (!current) {
        set({ stockCorrectionId: crypto.randomUUID() });
      }
    },

    resetStockCorrectionId: () => set({ stockCorrectionId: null }),

    reset: () => set(initialState),
  }),
);
